import { useEffect } from 'react';
import Header from '../../../shared/layouts-components/header/header';
import { getState } from '../../../shared/services/switcherServices';

const HEADER_ROOT_SELECTOR = '.app-header .main-header-container';
const LEGACY_LOGOUT_PATH = '/common/firebase/signin';
const CARES_AUTHENTICATED_HEADER_CLASS = 'cares-authenticated-header';

const normalizePathname = (pathname: string) => pathname.replace(/\/+$/, '') || '/';

const readUnreadCount = (root: ParentNode) => {
  const notificationSummary = root
    .querySelector('#notifiation-data')
    ?.textContent
    ?.trim()
    ?.toLowerCase();

  if (notificationSummary?.includes('unavailable')) {
    return null;
  }

  const badgeText = root
    .querySelector('.notifications-dropdown .header-icon-badge')
    ?.textContent
    ?.trim();
  const count = Number.parseInt(badgeText ?? '0', 10);

  return Number.isFinite(count) ? count : 0;
};

const getUserDisplayName = (root: ParentNode) =>
  root.querySelector('#mainHeaderProfile .fw-medium')?.textContent?.trim() || 'User';

const setAttributes = (element: Element | null, attributes: Record<string, string>) => {
  if (!(element instanceof HTMLElement)) {
    return;
  }

  Object.entries(attributes).forEach(([name, value]) => {
    element.setAttribute(name, value);
  });
};

const setIconHidden = (element: Element | null) => {
  if (!(element instanceof HTMLElement)) {
    return;
  }

  element.setAttribute('aria-hidden', 'true');
};

const applyAccessibilityAttributes = () => {
  const root = document.querySelector(HEADER_ROOT_SELECTOR);
  if (!root) {
    return;
  }

  const themeMode = getState().dataThemeMode;
  const unreadCount = readUnreadCount(root);
  const unreadLabel = unreadCount === null
    ? 'Open notifications menu, notifications unavailable'
    : unreadCount > 0
    ? `Open notifications menu, ${unreadCount} unread`
    : 'Open notifications menu';
  const userDisplayName = getUserDisplayName(root);
  const fullscreenLabel = document.fullscreenElement ? 'Exit full screen' : 'Enter full screen';

  setAttributes(root.querySelector('.sidemenu-toggle.header-link'), {
    'aria-label': 'Toggle navigation sidebar',
    role: 'button',
  });
  setAttributes(root.querySelector('.header-search'), {
    role: 'search',
  });
  setAttributes(root.querySelector('.header-search-bar'), {
    'aria-label': 'Search navigation results',
  });
  setAttributes(root.querySelector('.header-search-icon'), {
    'aria-hidden': 'true',
    tabindex: '-1',
  });
  setAttributes(root.querySelector('.header-element.d-md-none.d-block .header-link'), {
    'aria-label': 'Open search',
    role: 'button',
  });
  setAttributes(root.querySelector('.layout-setting'), {
    'aria-label': themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode',
    role: 'button',
  });
  setAttributes(root.querySelector('.notifications-dropdown .dropdown-toggle'), {
    'aria-label': unreadLabel,
    title: unreadLabel,
    role: 'button',
    tabindex: '0',
  });
  setAttributes(root.querySelector('.header-fullscreen .header-link'), {
    'aria-label': fullscreenLabel,
    role: 'button',
  });
  setAttributes(root.querySelector('#mainHeaderProfile'), {
    'aria-label': `Open account menu for ${userDisplayName}`,
    title: `Open account menu for ${userDisplayName}`,
    role: 'button',
    tabindex: '0',
  });

  root.querySelectorAll('.header-link-icon, .header-search-icon i, .dropdown-item-close1 i').forEach((icon) => {
    setIconHidden(icon);
  });

  const profileAvatar = root.querySelector('#mainHeaderProfile img.avatar');
  if (profileAvatar instanceof HTMLImageElement) {
    profileAvatar.alt = '';
  }

  const profileFallback = root.querySelector('#mainHeaderProfile .avatar-rounded.bg-primary-transparent');
  setAttributes(profileFallback, {
    'aria-hidden': 'true',
  });

  root.querySelectorAll('.dropdown-item-close1').forEach((control) => {
    const title = control
      .closest('.dropdown-item')
      ?.querySelector('.fw-medium a, .fw-medium')
      ?.textContent
      ?.trim();
    const isDisabled = control.classList.contains('disabled');

    setAttributes(control, {
      'aria-label': title ? `Mark notification "${title}" as read` : 'Mark notification as read',
      'aria-disabled': isDisabled ? 'true' : 'false',
      role: 'button',
      tabindex: isDisabled ? '-1' : '0',
    });
  });
};

const CaresAuthenticatedHeader = () => {
  useEffect(() => {
    document.body.classList.add(CARES_AUTHENTICATED_HEADER_CLASS);

    const apply = () => {
      window.requestAnimationFrame(applyAccessibilityAttributes);
    };

    const handleClickableKeydown = (event: KeyboardEvent) => {
      if (event.key !== ' ' && event.key !== 'Enter') {
        return;
      }

      const target = event.target;
      if (!(target instanceof HTMLElement) || target.getAttribute('role') !== 'button') {
        return;
      }

      if (target instanceof HTMLButtonElement) {
        return;
      }

      if (!target.closest(HEADER_ROOT_SELECTOR)) {
        return;
      }

      if (target instanceof HTMLAnchorElement) {
        const href = target.getAttribute('href') ?? '';
        if (href !== '#' && href !== '') {
          return;
        }
      }

      event.preventDefault();
      target.click();
    };

    const handleHashLinkClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const link = target.closest('a.header-link[href="#"], a.dropdown-item-close1[href="#"]');
      if (!link) {
        return;
      }

      event.preventDefault();
    };

    const handleLogoutLinkClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const logoutLink = target.closest('.header-profile-dropdown .dropdown-item');
      if (!(logoutLink instanceof HTMLAnchorElement)) {
        return;
      }

      const logoutUrl = new URL(logoutLink.href, window.location.origin);
      if (normalizePathname(logoutUrl.pathname) !== LEGACY_LOGOUT_PATH) {
        return;
      }

      event.preventDefault();

      const csrfToken =
        document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/logout';
      form.style.display = 'none';

      const tokenInput = document.createElement('input');
      tokenInput.type = 'hidden';
      tokenInput.name = '_token';
      tokenInput.value = csrfToken;
      form.appendChild(tokenInput);

      document.body.appendChild(form);
      form.submit();
    };

    apply();

    const root = document.querySelector(HEADER_ROOT_SELECTOR);

    const observer = new MutationObserver(() => {
      apply();
    });

    if (root) {
      observer.observe(root, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
      });
    }

    document.addEventListener('fullscreenchange', apply);
    document.addEventListener('keydown', handleClickableKeydown);
    document.addEventListener('click', handleHashLinkClick, true);
    document.addEventListener('click', handleLogoutLinkClick, true);

    return () => {
      observer.disconnect();
      document.body.classList.remove(CARES_AUTHENTICATED_HEADER_CLASS);
      document.removeEventListener('fullscreenchange', apply);
      document.removeEventListener('keydown', handleClickableKeydown);
      document.removeEventListener('click', handleHashLinkClick, true);
      document.removeEventListener('click', handleLogoutLinkClick, true);
    };
  }, []);

  return <Header />;
};

export default CaresAuthenticatedHeader;
